import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import PaymentMethodDetails from './PaymentMethodDetails';
import getExpectedBalance from './getExpectedBalance';

const SummaryItemBox = ({ children, ...rest }: BoxProps) => (
	<Box
		sx={{
			paddingBottom: 150,
			borderBottom: '1px solid',
			borderColor: ({ vars }) => vars.palette.uc.border.weak,
			'&:last-child': {
				paddingBottom: 0,
				borderBottom: 'none',
			},
		}}
		{...rest}
	>
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: 100,
			}}
		>
			{children}
		</Box>
	</Box>
);

export interface ConfirmPaymentContentProps {
	accountBalance: number;
	amount: number;
	isSubmitting?: boolean;
	onSubmit: () => void;
	paymentMethod: string | undefined;
	issuer?: string;
	label: string;
	secondaryLabel?: string;
}

const ConfirmPaymentContent = ({
	accountBalance,
	amount,
	isSubmitting,
	onSubmit,
	paymentMethod,
	issuer,
	label,
	secondaryLabel,
}: ConfirmPaymentContentProps) => {
	const { expectedCredit, expectedRemainingBalance, hasExpectedCredit } =
		getExpectedBalance(amount, accountBalance);

	return (
		<Stack
			sx={{
				gap: 300,
			}}
		>
			<Stack sx={{ gap: 150 }}>
				<SummaryItemBox>
					<Typography
						variant="body-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						Method
					</Typography>

					<Box
						sx={{
							paddingX: 150,
							paddingY: 100,
							borderRadius: ({ borderRadius }) => borderRadius.rounded,
							border: '1px solid',
							borderColor: ({ vars }) => vars.palette.uc.border.weak,
						}}
					>
						<PaymentMethodDetails
							paymentMethod={paymentMethod}
							issuer={issuer}
							label={label}
							secondaryLabel={secondaryLabel}
						/>
					</Box>
				</SummaryItemBox>

				<SummaryItemBox>
					<Typography
						variant="body-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						Amount
					</Typography>

					<Typography variant="body-xl" sx={{ fontWeight: 500 }}>
						{formatCurrency(amount)}
					</Typography>
				</SummaryItemBox>

				<SummaryItemBox>
					<Typography
						variant="body-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						Remaining balance
					</Typography>

					<Typography variant="body-xl" sx={{ fontWeight: 500 }}>
						{formatCurrency(expectedRemainingBalance)}
					</Typography>
				</SummaryItemBox>

				{hasExpectedCredit && (
					<SummaryItemBox>
						<Typography
							variant="body-md"
							sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
						>
							Account credit added
						</Typography>

						<Typography variant="body-xl" sx={{ fontWeight: 500 }}>
							{formatCurrency(expectedCredit, { plusSign: true })}
						</Typography>
					</SummaryItemBox>
				)}
			</Stack>

			<Button
				variant="primary"
				size="large"
				color="highlight"
				onClick={onSubmit}
				loading={isSubmitting}
			>
				Pay now
			</Button>
		</Stack>
	);
};

export default ConfirmPaymentContent;
